import { FlowRoute, FlowRouteCategory } from '@eneco/flows/types';

import { FlowConsumerContextEnergy } from '../../context';
import { shouldSkipWhenOnlyGeothermalHeating } from '../../eneco/skippingRules';
import {
  shouldSkipWhenNoElectricity,
  shouldSkipWhenNoSolarPanels,
  shouldSkipWhenUsageIsKnown,
  shouldSkipWhenUsageNotKnown,
} from '../skippingRules';
import {
  AddressStep,
  BuildingAgeStep,
  BuildingTypeStep,
  MeterTypeStep,
  ResidentsStep,
  SolarPanelsRedeliveryStep,
  SolarPanelsStep,
  UsageKnownStep,
  UsageStep,
} from '../steps';

export const addressRoute: FlowRoute<FlowConsumerContextEnergy> = {
  id: 'ADDRESS',
  path: '/adres/',
  category: FlowRouteCategory.Calculation,
  component: AddressStep,
  skippingFields: ['postalCode', 'houseNumber'],
};

interface CreateUsageCalculationRoutesOptions {
  /** Eneco offers warmth/geothermal-only products; Oxxio doesn't, so it never needs these rules. */
  includeWarmthRules: boolean;
}

export const createUsageCalculationRoutes = ({
  includeWarmthRules,
}: CreateUsageCalculationRoutesOptions): FlowRoute<FlowConsumerContextEnergy>[] => {
  const warmthRules = includeWarmthRules ? [shouldSkipWhenOnlyGeothermalHeating] : [];

  return [
    {
      id: 'USAGE_KNOWN',
      path: '/verbruik-bekend/',
      category: FlowRouteCategory.Calculation,
      component: UsageKnownStep,
      skippingRules: warmthRules,
    },

    {
      id: 'USAGE',
      path: '/verbruik/',
      category: FlowRouteCategory.Calculation,
      component: UsageStep,
      actions: ['setUsageKnown'],
      skippingRules: [shouldSkipWhenUsageNotKnown, ...warmthRules],
    },

    {
      id: 'RESIDENTS',
      path: '/inschatting/aantal-bewoners/',
      category: FlowRouteCategory.Calculation,
      component: ResidentsStep,
      skippingRules: [shouldSkipWhenUsageIsKnown, ...warmthRules],
    },

    {
      id: 'BUILDING_TYPE',
      path: '/inschatting/type-woning/',
      category: FlowRouteCategory.Calculation,
      component: BuildingTypeStep,
      skippingRules: [shouldSkipWhenUsageIsKnown, ...warmthRules],
    },

    {
      id: 'BUILDING_AGE',
      path: '/inschatting/bouwjaar-woning/',
      category: FlowRouteCategory.Calculation,
      component: BuildingAgeStep,
      actions: ['resetUsageValues', 'setUsageEstimatedValues'],
      skippingRules: [shouldSkipWhenUsageIsKnown, ...warmthRules],
    },

    {
      id: 'METER_TYPE',
      path: '/inschatting/metertype/',
      category: FlowRouteCategory.Calculation,
      component: MeterTypeStep,
      actions: ['resetUsageValues', 'setUsageEstimatedValues'],
      skippingRules: [shouldSkipWhenUsageIsKnown, shouldSkipWhenNoElectricity],
    },

    {
      id: 'SOLAR_PANELS',
      path: '/zonnepanelen/',
      category: FlowRouteCategory.Calculation,
      component: SolarPanelsStep,
      actions: ['resetSolarPanelsOutput'],
      skippingRules: [shouldSkipWhenNoElectricity],
    },

    {
      id: 'SOLAR_PANELS_REDELIVERY',
      path: '/zonnepanelen/verbruik/',
      category: FlowRouteCategory.Calculation,
      component: SolarPanelsRedeliveryStep,
      skippingRules: [shouldSkipWhenNoSolarPanels],
    },
  ];
};
